import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MentionUtilsService } from 'src/app/core/services/helper/mention-utils.service';
import { MentionUser } from '../../interface/comment-mention';

@Component({
  selector: 'ah-mention-input',
  templateUrl: './mention-input.component.html',
  styleUrls: ['./mention-input.component.scss']
})
export class MentionInputComponent {

  @Input() users: MentionUser[] = [];
  @Input() placeholder: string = '';
  @Input() readonly: boolean = false;
  @Input() value: string = '';
  @Input() mentions: MentionUser[] = [];
  @Output() valueChange = new EventEmitter<string>();
  @Output() mentionsChange = new EventEmitter<MentionUser[]>();
  @Output() enter = new EventEmitter<void>();
  @ViewChild('inputRef') inputRef: ElementRef<HTMLInputElement>;

  suggestions: MentionUser[] = [];
  activeIndex: number = 0;

  private mentionStart: number = -1;
  private readonly maxSuggestions = 8;

  constructor(
    private mentionUtils: MentionUtilsService,
  ) { }

  onInput(text: string) {
    this.value = text;
    this.valueChange.emit(text);
    const mentions = this.mentionUtils.syncMentions(text, this.mentions || []);
    if (mentions.length != (this.mentions || []).length) {
      this.mentions = mentions;
      this.mentionsChange.emit(mentions);
    }
    this.updateSuggestions();
  }

  onKeydown(event: KeyboardEvent) {
    if (!this.suggestions.length) {
      if (event.key == 'Enter') {
        this.enter.emit();
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex = (this.activeIndex + 1) % this.suggestions.length;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex = (this.activeIndex - 1 + this.suggestions.length) % this.suggestions.length;
        break;
      case 'Enter':
      case 'Tab':
        event.preventDefault();
        this.selectUser(this.suggestions[this.activeIndex]);
        break;
      case 'Escape':
        event.preventDefault();
        this.closeSuggestions();
        break;
    }
  }

  updateSuggestions() {
    const input = this.inputRef?.nativeElement;
    const caret = input?.selectionStart ?? (this.value || '').length;
    const beforeCaret = (this.value || '').slice(0, caret);
    const match = beforeCaret.match(/(^|\s)@([^@\n]{0,30})$/);
    if (!match) {
      this.closeSuggestions();
      return;
    }
    const query = match[2].toLowerCase();
    this.mentionStart = caret - match[2].length - 1;
    this.suggestions = (this.users || [])
      .filter((x) => x.name?.toLowerCase().includes(query))
      .slice(0, this.maxSuggestions);
    this.activeIndex = 0;
  }

  selectUser(user: MentionUser) {
    const input = this.inputRef.nativeElement;
    const text = this.value || '';
    const caret = input.selectionStart ?? text.length;
    const mentionText = `@${user.name} `;
    const updated = text.slice(0, this.mentionStart) + mentionText + text.slice(caret);
    const mentions = (this.mentions || []).some((x) => x.id == user.id) ? this.mentions : [...(this.mentions || []), user];
    this.value = updated;
    this.valueChange.emit(updated);
    this.mentions = mentions;
    this.mentionsChange.emit(mentions);
    const position = this.mentionStart + mentionText.length;
    this.closeSuggestions();
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(position, position);
    });
  }

  closeSuggestions() {
    this.suggestions = [];
    this.activeIndex = 0;
    this.mentionStart = -1;
  }

  onBlur() {
    setTimeout(() => this.closeSuggestions(), 150);
  }

  initials(name: string): string {
    return (name || '').split(' ').filter((x) => x).slice(0, 2).map((x) => x[0]).join('').toUpperCase();
  }
}
